import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Flame, ShieldCheck, Check, Clock } from "lucide-react";

export default function PublishLiving() {
  const { code } = useParams();
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/living-candle/g/${code}`).then((r) => setInfo(r.data)).catch(() => setError(t("po.ac_err_invalid")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const confirm = async () => {
    if (!window.confirm(t("po.pl_confirm1"))) return;
    if (!window.confirm(t("po.pl_confirm2"))) return;
    setBusy(true);
    try {
      await api.post(`/living-candle/g/${code}/confirm`);
      toast.success(t("po.pl_toast_ok"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("po.pl_toast_fail")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="mx-auto max-w-lg px-5 py-12" data-testid="publish-living-page">
        {error ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-red-300" data-testid="publish-living-error">{error}</div>
        ) : !info ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-amber-300" /></div>
        ) : (
          <div className="rounded-3xl border border-amber-400/20 bg-amber-500/[0.05] p-6 text-center" data-testid="publish-living-confirm">
            <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 px-3 py-1 text-xs text-amber-200"><ShieldCheck size={14} /> {t("po.pl_secure_link")}</div>
            <Flame className="mx-auto mb-3 text-amber-300" size={36} />
            <h1 className="text-xl font-semibold text-foreground">{t("po.ac_tribute_to", { name: info.display_name })}</h1>

            <p className="mt-2 text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t("po.pl_intro", { name: info.garante_name ? `${info.garante_name}, ` : "", n: info.garantes_total }) }} />

            <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-500/[0.08] p-4 text-left text-sm text-amber-100/90" data-testid="publish-living-alert">
              <div className="mb-1.5 flex items-center gap-2 font-medium text-amber-200"><ShieldCheck size={15} /> {t("po.pl_alert_title")}</div>
              <ul className="list-disc space-y-1 pl-5">
                <li dangerouslySetInnerHTML={{ __html: t("po.pl_alert_1") }} />
                <li dangerouslySetInnerHTML={{ __html: t("po.pl_alert_2") }} />
                <li dangerouslySetInnerHTML={{ __html: t("po.pl_alert_3") }} />
                <li dangerouslySetInnerHTML={{ __html: t("po.pl_alert_4") }} />
              </ul>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-3 text-sm text-white/90" data-testid="publish-living-status">
              <span className="font-medium text-amber-200">{info.confirmed_count}</span>{t("po.pl_status_rest", { total: info.garantes_total })}
            </div>

            {info.already_confirmed ? (
              <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="publish-living-confirmed">
                <Check className="mx-auto mb-1" size={22} /> {t("po.pl_confirmed_saved")}
                {info.death_confirmed ? (
                  <p className="mt-2 flex items-center justify-center gap-1.5 text-amber-200"><Clock size={14} /> {t("po.pl_all_confirmed")}</p>
                ) : (
                  <p className="mt-2 text-muted-foreground">{t("po.pl_waiting_others")}</p>
                )}
              </div>
            ) : (
              <Button className="mt-5 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={confirm} disabled={busy} data-testid="publish-living-confirm-btn">
                {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} {t("po.pl_confirm_btn")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
